import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, Star, StarOff, MoreVertical, Edit, Trash2, Send, User, Phone, Mail, Tag } from 'lucide-react';
import axios from 'axios';
import { API } from '../../utils/api';
import { apiErrorMessage } from '../../utils/swishReturn';
import { useAuthContext } from '../../context/AuthContext';

const SWISH_RE = /^(07\d{8}|467\d{8}|123\d{7})$/;
const swishDigits = (v) => String(v || '').replace(/\D/g, '');
const swishInvalid = (v) => !!v && !SWISH_RE.test(v);

export function P2PContacts({ onRequestFromContact }) {
  const { headers } = useAuthContext();
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    payment_alias: '',
    email: '',
    tags: '',
    is_favorite: false
  });

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importContacts, setImportContacts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importFileName, setImportFileName] = useState('');

  const fetchContacts = async () => {
    try {
      let url = `${API}/contact`;
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (showFavoritesOnly) params.append('favorites_only', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, { headers });
      setContacts(res.data?.items || []);
    } catch (e) {
      console.error('Failed to fetch contacts:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${API}/contact/suggestions`, { headers });
      setSuggestions(res.data?.items || []);
    } catch (e) {
      console.error('Failed to fetch suggestions:', e);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchSuggestions();
  }, [headers.Authorization]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, showFavoritesOnly]);

  const resetForm = () => {
    setForm({
      name: '',
      payment_alias: '',
      email: '',
      tags: '',
      is_favorite: false
    });
  };

  const parseCsvContacts = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const getVal = (cols, key) => {
      const idx = headers.indexOf(key);
      return idx >= 0 ? cols[idx] : '';
    };

    return lines.slice(1).map((line) => {
      const cols = line.split(',').map(c => c.trim());
      const name = getVal(cols, 'name') || getVal(cols, 'full_name');
      const paymentAlias = getVal(cols, 'phone') || getVal(cols, 'swish') || getVal(cols, 'payment_alias') || getVal(cols, 'alias');
      const email = getVal(cols, 'email');
      const tagsRaw = getVal(cols, 'tags');
      const favoriteRaw = getVal(cols, 'favorite');

      if (!name) return null;

      return {
        name,
        payment_alias: paymentAlias || undefined,
        email: email || undefined,
        tags: tagsRaw ? tagsRaw.split(';').map(t => t.trim()).filter(Boolean) : [],
        is_favorite: ['true', 'yes', '1'].includes((favoriteRaw || '').toLowerCase())
      };
    }).filter(Boolean);
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    const text = await file.text();
    const parsed = parseCsvContacts(text);
    setImportContacts(parsed);
  };

  const handleImportContacts = async () => {
    if (importContacts.length === 0) {
      toast.error('No valid contacts to import');
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${API}/contact/batch`, {
        contacts: importContacts
      }, { headers });
      toast.success(`Imported ${res.data.created} contacts`);
      if (res.data.failed > 0) {
        toast.message(`${res.data.failed} contacts skipped`);
      }
      setImportDialogOpen(false);
      setImportContacts([]);
      setImportFileName('');
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const handleCreateContact = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        payment_alias: form.payment_alias || undefined,
        email: form.email || undefined,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        is_favorite: form.is_favorite
      };

      await axios.post(`${API}/contact`, payload, { headers });
      toast.success('Contact created!');
      setCreateDialogOpen(false);
      resetForm();
      fetchContacts();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create contact'));
    }
  };

  const handleEditContact = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        payment_alias: form.payment_alias || undefined,
        email: form.email || undefined,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        is_favorite: form.is_favorite
      };

      await axios.put(`${API}/contact/${editingContact._id}`, payload, { headers });
      toast.success('Contact updated!');
      setEditDialogOpen(false);
      setEditingContact(null);
      resetForm();
      fetchContacts();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update contact'));
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await axios.delete(`${API}/contact/${contactId}`, { headers });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const handleToggleFavorite = async (contact) => {
    try {
      await axios.put(`${API}/contact/${contact._id}`, {
        is_favorite: !contact.is_favorite
      }, { headers });
      fetchContacts();
    } catch (e) {
      console.error('Failed to toggle favorite:', e);
    }
  };

  const handleAddSuggestion = async (suggestion) => {
    try {
      const payload = {
        name: suggestion.name,
        payment_alias: suggestion.payment_alias,
        email: suggestion.email,
        tags: [],
        is_favorite: false
      };

      await axios.post(`${API}/contact`, payload, { headers });
      toast.success('Contact added from suggestions!');
      fetchContacts();
      fetchSuggestions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add contact');
    }
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name,
      payment_alias: swishDigits(contact.payment_alias),
      email: contact.email || '',
      tags: contact.tags?.join(', ') || '',
      is_favorite: contact.is_favorite
    });
    setEditDialogOpen(true);
  };

  const favoriteContacts = contacts.filter(c => c.is_favorite);
  const regularContacts = contacts.filter(c => !c.is_favorite);

  return (
    <div className="space-y-6" data-testid="p2p-contacts">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Contacts</h2>
          <p className="text-sm text-muted-foreground">Manage your payment contacts</p>
        </div>
        <div className="flex items-center gap-2">
          {/* CSV Import hidden in V5.0 */}

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
                <DialogDescription>
                  Add someone to quickly request payments from them
                </DialogDescription>
              </DialogHeader>
              <ContactForm form={form} setForm={setForm} />
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }} data-testid="cancel-create-contact-btn">
                  Cancel
                </Button>
                <Button data-testid="save-contact-btn" onClick={handleCreateContact} disabled={swishInvalid(form.payment_alias)}>
                  Save Contact
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-contacts"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="favorites-only" className="text-sm text-muted-foreground">
            Favorites only
          </Label>
          <Switch
            id="favorites-only"
            checked={showFavoritesOnly}
            onCheckedChange={setShowFavoritesOnly}
            data-testid="favorites-toggle"
          />
        </div>
      </div>

      {/* Suggestions from Transaction History */}
      {suggestions.length > 0 && !showFavoritesOnly && !searchQuery && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Suggested Contacts</CardTitle>
            <CardDescription className="text-xs">
              Based on your transaction history
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {suggestions.slice(0, 5).map((s, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddSuggestion(s)}
                  data-testid={`suggestion-${idx}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {s.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contacts List */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No contacts yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add contacts to easily request payments
            </p>
            <Button className="mt-4" onClick={() => setCreateDialogOpen(true)} data-testid="add-first-contact-btn">
              Add your first contact
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Favorite Contacts */}
          {favoriteContacts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                Favorites
              </h3>
              <div className="grid gap-2">
                {favoriteContacts.map((contact) => (
                  <ContactCard
                    key={contact._id}
                    contact={contact}
                    onEdit={() => openEditDialog(contact)}
                    onDelete={() => handleDeleteContact(contact._id)}
                    onToggleFavorite={() => handleToggleFavorite(contact)}
                    onRequestPayment={onRequestFromContact}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular Contacts */}
          {regularContacts.length > 0 && !showFavoritesOnly && (
            <div>
              {favoriteContacts.length > 0 && (
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  All Contacts
                </h3>
              )}
              <div className="grid gap-2">
                {regularContacts.map((contact) => (
                  <ContactCard
                    key={contact._id}
                    contact={contact}
                    onEdit={() => openEditDialog(contact)}
                    onDelete={() => handleDeleteContact(contact._id)}
                    onToggleFavorite={() => handleToggleFavorite(contact)}
                    onRequestPayment={onRequestFromContact}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          <ContactForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingContact(null); resetForm(); }} data-testid="cancel-edit-contact-btn">
              Cancel
            </Button>
            <Button data-testid="update-contact-btn" onClick={handleEditContact} disabled={swishInvalid(form.payment_alias)}>
              Update Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Contact Form Component
function ContactForm({ form, setForm }) {
  const { t } = useTranslation();
  const invalid = swishInvalid(form.payment_alias);
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          data-testid="contact-name-input"
          placeholder="John Doe"
          value={form.name}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="payment_alias">Swish Number</Label>
        <Input
          id="payment_alias"
          data-testid="contact-alias-input"
          placeholder="0701234567"
          inputMode="numeric"
          aria-invalid={invalid}
          value={form.payment_alias}
          onChange={(e) => setForm(prev => ({ ...prev, payment_alias: swishDigits(e.target.value) }))}
        />
        {invalid ? (
          <p className="text-xs text-red-600" data-testid="contact-alias-error">{t('contactsForm.swishInvalid')}</p>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="contact-alias-hint">{t('contactsForm.swishHint')}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          data-testid="contact-email-input"
          type="email"
          placeholder="john@example.com"
          value={form.email}
          onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags">Tags (comma-separated)</Label>
        <Input
          id="tags"
          data-testid="contact-tags-input"
          placeholder="friend, work, family"
          value={form.tags}
          onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
        />
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="favorite">Mark as Favorite</Label>
        <Switch
          id="favorite"
          data-testid="contact-favorite-switch"
          checked={form.is_favorite}
          onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_favorite: checked }))}
        />
      </div>
    </div>
  );
}

// Contact Card Component
function ContactCard({ contact, onEdit, onDelete, onToggleFavorite, onRequestPayment }) {
  return (
    <Card data-testid={`contact-card-${contact._id}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
            <User className="h-5 w-5 text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium truncate">{contact.name}</p>
              {contact.is_favorite && (
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {contact.payment_alias && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {contact.payment_alias}
                </span>
              )}
              {contact.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="h-3 w-3" />
                  {contact.email}
                </span>
              )}
            </div>
            {contact.tags?.length > 0 && (
              <div className="flex gap-1 mt-1">
                {contact.tags.slice(0, 3).map((tag, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px]">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onRequestPayment && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRequestPayment(contact)}
                data-testid={`request-from-${contact._id}`}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`contact-menu-${contact._id}`}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onToggleFavorite} data-testid={`toggle-favorite-${contact._id}`}>
                  {contact.is_favorite ? (
                    <>
                      <StarOff className="h-4 w-4 mr-2" />
                      Remove from favorites
                    </>
                  ) : (
                    <>
                      <Star className="h-4 w-4 mr-2" />
                      Add to favorites
                    </>
                  )}
                </DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit} data-testid={`edit-contact-${contact._id}`}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid={`delete-contact-${contact._id}`}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {contact.transaction_count > 0 && (
          <p className="text-xs text-muted-foreground mt-2 pl-13">
            {contact.transaction_count} transaction{contact.transaction_count > 1 ? 's' : ''}
            {contact.last_transaction_at && ` • Last: ${new Date(contact.last_transaction_at).toLocaleDateString()}`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
